/**
 * HooksPipeline Component
 * Main component for managing hook pipeline with drag-and-drop
 * Changes are saved via the parent's Save button (unified save mode)
 *
 * Feature toggles are integrated directly into the system stage.
 * Virtual feature hooks (storage, memory, knowledge, team, debug) are shown
 * alongside real hooks from the backend.
 */

import React, { useState, useMemo } from 'react';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import {
  Brain,
  Database,
  BookOpen,
  Users,
  Bug,
  ChevronRight,
  AlertTriangle,
  type LucideIcon,
} from 'lucide-react';
import { HookStage } from './HookStage';
import { HookConfigPanel } from './HookConfigPanel';
import { HookItem } from './HookItem';
import { useHooksPipeline } from '../../lib/hooks/useHooksPipeline';
import { useHookDragDrop, calculateNewSortOrders } from '../../lib/hooks/useHookDragDrop';
import { useI18n } from '@/i18n';
import { getConfigValue } from '@/config/fieldConfig';
import { isFeatureEnabled, getFeatureToggleValue } from '@/config/featureHooks';
import type { HookConfig, HookStage as HookStageType } from '../../types/hooks';
import type { AgentConfiguration } from '@/types';
import type { TabId } from '@/config/tabConfig';

// Icon mapping for virtual features
const FEATURE_ICONS: Record<string, LucideIcon> = {
  Brain,
  Database,
  BookOpen,
  Users,
  Bug,
};

/**
 * Virtual feature hooks that are displayed in the system stage
 * These don't have real backend hooks but control config directly
 *
 * NOTE: classifier is NOT included here because it has real backend hooks.
 * astrology_plus is a merged hook that combines birth_chart and transit.
 */
interface VirtualFeatureHook {
  id: string;
  displayName: string;
  description: string;
  icon: string;
  configPath: string;
  /** Additional config paths to control together (for merged features) */
  additionalConfigPaths?: string[];
  linkedTab?: TabId;
  sortOrder: number;
  /** If true, toggle directly enables/disables; otherwise it expands the tab */
  directToggle?: boolean;
}

const VIRTUAL_FEATURE_HOOKS: VirtualFeatureHook[] = [
  {
    id: 'storage',
    displayName: '会话存储',
    description: '存储聊天会话历史记录',
    icon: 'Database',
    configPath: 'storage_enabled',
    sortOrder: -50,
    directToggle: true,
  },
  {
    id: 'memory',
    displayName: '记忆系统',
    description: '长期记忆存储和检索',
    icon: 'Brain',
    configPath: 'memory.enabled',
    linkedTab: 'memory',
    sortOrder: -40,
  },
  // NOTE: knowledge removed - use knowledge_context hook in USER stage instead
  // The hook is mapped to 'knowledge' tab via HOOK_TO_TAB_MAPPING
  {
    id: 'team',
    displayName: '团队模式',
    description: '多Agent协作',
    icon: 'Users',
    configPath: 'agent_mode',
    sortOrder: -20,
    directToggle: true, // One-click toggle: 'single' <-> 'team'
  },
  {
    id: 'debug',
    displayName: '调试模式',
    description: '在响应中显示详细执行信息',
    icon: 'Bug',
    configPath: 'debug_mode',
    sortOrder: -10,
    directToggle: true,
  },
];

/**
 * Mapping from real hook names to their linked tabs
 * These hooks exist in backend but also control tab visibility
 *
 * When a hook has a linked tab:
 * - Toggling visibility in pipeline expands/collapses the tab
 * - The actual "enabled" toggle is INSIDE the tab
 */
const HOOK_TO_TAB_MAPPING: Record<string, TabId> = {
  astrology_plus: 'astrology',
  emotion_enhance: 'emotion',
  classifier: 'classifier',
  knowledge_context: 'knowledge',
};

/**
 * Hook dependencies - defines which hooks depend on other hooks
 * When disabling a hook that others depend on, show a confirmation dialog
 *
 * Note: We check hooks_visible (visibility in pipeline) not hooks.*.enabled
 * because the toggle controls visibility, and visibility implies the hook is active
 */
const HOOK_DEPENDENTS: Record<string, {
  dependentHook: string;
  dependentDisplayName: string;
}> = {
  skill_tracking: {
    dependentHook: 'skill_dedup',
    dependentDisplayName: '技能去重',
  },
};

/**
 * Dependency mapping for enable confirmation
 * skill_dedup depends on skill_tracking, so when enabling skill_dedup, check if skill_tracking is enabled
 * If skill_tracking is OFF, offer to enable it first
 */
const HOOK_REQUIRES: Record<string, {
  requiredHook: string;
  requiredDisplayName: string;
}> = {
  skill_dedup: {
    requiredHook: 'skill_tracking',
    requiredDisplayName: '技能追踪',
  },
};

/**
 * VirtualFeatureItem Component
 * Renders a virtual feature hook in the pipeline (non-draggable)
 */
interface VirtualFeatureItemProps {
  feature: VirtualFeatureHook;
  config: Partial<AgentConfiguration>;
  onConfigChange: (path: string, value: any) => void;
  expandedTabs: Set<TabId>;
  onToggleExpansion: (tabId: TabId) => void;
  onNavigateToTab?: (tabId: string) => void;
}

const VirtualFeatureItem: React.FC<VirtualFeatureItemProps> = ({
  feature,
  config,
  onConfigChange,
  expandedTabs,
  onToggleExpansion,
  onNavigateToTab,
}) => {
  const Icon = FEATURE_ICONS[feature.icon] || Database;
  const configValue = getConfigValue(config, feature.configPath);

  // For features with additionalConfigPaths, check if ANY of them are enabled
  const checkAnyEnabled = () => {
    if (isFeatureEnabled(feature.id as any, configValue)) return true;
    if (feature.additionalConfigPaths) {
      for (const path of feature.additionalConfigPaths) {
        const val = getConfigValue(config, path);
        if (isFeatureEnabled(feature.id as any, val)) return true;
      }
    }
    return false;
  };
  const isActuallyEnabled = checkAnyEnabled();

  // For expandable features, check if tab is expanded
  const isExpanded = feature.linkedTab ? expandedTabs.has(feature.linkedTab) : false;
  const isToggled = feature.directToggle ? isActuallyEnabled : isExpanded;

  const handleToggle = () => {
    if (feature.directToggle) {
      // Direct toggle: modify config directly (and additional paths)
      const newValue = getFeatureToggleValue(feature.id as any, !isActuallyEnabled);
      onConfigChange(feature.configPath, newValue);
      // Also toggle additional config paths
      if (feature.additionalConfigPaths) {
        for (const path of feature.additionalConfigPaths) {
          onConfigChange(path, newValue);
        }
      }
      // If has linkedTab, also expand/collapse it
      if (feature.linkedTab) {
        const willBeEnabled = !isActuallyEnabled;
        if (willBeEnabled && !isExpanded) {
          // Enabling: expand tab
          onToggleExpansion(feature.linkedTab);
        } else if (!willBeEnabled && isExpanded) {
          // Disabling: collapse tab (no confirmation needed since we're disabling)
          onToggleExpansion(feature.linkedTab);
        }
      }
    } else if (feature.linkedTab) {
      // Expandable feature with linked tab (no directToggle)
      // When expanding: also enable the feature
      // When collapsing: onToggleExpansion handles confirmation and disabling
      if (!isExpanded) {
        // Expanding: enable the feature AND expand the tab
        const newValue = getFeatureToggleValue(feature.id as any, true);
        onConfigChange(feature.configPath, newValue);
        if (feature.additionalConfigPaths) {
          for (const path of feature.additionalConfigPaths) {
            onConfigChange(path, newValue);
          }
        }
      }
      // Toggle tab expansion (collapse will trigger confirmation if feature is enabled)
      onToggleExpansion(feature.linkedTab);
    }
  };

  // Icon color based on state
  const getIconClasses = () => {
    if (feature.directToggle) {
      return isActuallyEnabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400';
    }
    // Expandable feature
    if (isExpanded && isActuallyEnabled) {
      return 'bg-green-100 text-green-600';
    }
    if (isExpanded) {
      return 'bg-blue-100 text-blue-600';
    }
    return 'bg-gray-100 text-gray-400';
  };

  const toggleClasses = [
    'w-10 h-5 rounded-full transition-colors relative cursor-pointer',
    isToggled ? 'bg-green-500' : 'bg-gray-300',
  ].join(' ');

  const toggleKnobClasses = [
    'w-4 h-4 bg-white rounded-full shadow transform transition-transform absolute top-0.5',
    isToggled ? 'translate-x-5' : 'translate-x-0.5',
  ].join(' ');

  return (
    <div className="flex items-center gap-2 p-2 bg-white border rounded hover:bg-gray-50 transition-colors">
      {/* Empty space for alignment with draggable hooks */}
      <div className="w-4 h-4" />

      {/* Toggle */}
      <button
        type="button"
        className={toggleClasses}
        onClick={handleToggle}
        title={feature.directToggle ? '启用/禁用功能' : '展开/收起配置'}
      >
        <div className={toggleKnobClasses} />
      </button>

      {/* Icon */}
      <div className={`p-1.5 rounded ${getIconClasses()}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`font-medium text-sm ${isToggled ? 'text-gray-900' : 'text-gray-500'}`}>
            {feature.displayName}
          </span>
          {/* Status badges */}
          {!feature.directToggle && isExpanded && (
            <span className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded border ${
              isActuallyEnabled
                ? 'bg-green-50 text-green-600 border-green-100'
                : 'bg-yellow-50 text-yellow-600 border-yellow-100'
            }`}>
              {isActuallyEnabled ? '已启用' : '待配置'}
            </span>
          )}
          {feature.directToggle && isActuallyEnabled && (
            <span className="inline-flex items-center px-1.5 py-0.5 text-xs bg-green-50 text-green-600 rounded border border-green-100">
              已启用
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500 truncate">{feature.description}</div>
      </div>

      {/* Navigate to tab button */}
      {feature.linkedTab && isExpanded && onNavigateToTab && (
        <button
          type="button"
          onClick={() => onNavigateToTab(feature.linkedTab!)}
          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
          title={`前往 ${feature.displayName} 配置`}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}

      {/* Sort order placeholder for alignment */}
      <span className="text-xs text-gray-300 font-mono">虚拟</span>
    </div>
  );
};

/**
 * SystemStageWithVirtualFeatures Component
 * Custom system stage that includes virtual features at the top
 */
interface SystemStageWithVirtualFeaturesProps {
  hooks: HookConfig[];
  selectedHook: HookConfig | null;
  isDragging: boolean;
  onSelectHook: (hook: HookConfig) => void;
  onToggleVisibility: (hook: HookConfig) => void;
  translations: any;
  localConfig: Partial<AgentConfiguration>;
  onConfigChange: (path: string, value: any) => void;
  expandedTabs: Set<TabId>;
  onToggleExpansion: (tabId: TabId) => void;
  onNavigateToTab?: (tabId: string) => void;
}

const SystemStageWithVirtualFeatures: React.FC<SystemStageWithVirtualFeaturesProps> = ({
  hooks,
  selectedHook,
  isDragging,
  onSelectHook,
  onToggleVisibility,
  translations: t,
  localConfig,
  onConfigChange,
  expandedTabs,
  onToggleExpansion,
  onNavigateToTab,
}) => {
  const sortedHooks = [...hooks].sort((a, b) => a.sort_order - b.sort_order);
  const totalCount = VIRTUAL_FEATURE_HOOKS.length + hooks.length;

  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      {/* Stage Header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="text-gray-600">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
            />
          </svg>
        </div>
        <h4 className="font-medium">{t.stages.system}</h4>
        <span className="text-xs text-gray-500">({t.hooksCount.replace('{count}', String(totalCount))})</span>
      </div>

      <div className="space-y-2">
        {/* Virtual Features Section */}
        <div className="space-y-2">
          {VIRTUAL_FEATURE_HOOKS.map((feature) => (
            <VirtualFeatureItem
              key={feature.id}
              feature={feature}
              config={localConfig}
              onConfigChange={onConfigChange}
              expandedTabs={expandedTabs}
              onToggleExpansion={onToggleExpansion}
              onNavigateToTab={onNavigateToTab}
            />
          ))}
        </div>

        {/* Divider between virtual and real hooks */}
        {sortedHooks.length > 0 && (
          <div className="flex items-center gap-2 py-1">
            <div className="flex-1 border-t border-dashed border-gray-300" />
            <span className="text-xs text-gray-400">扩展 Hooks</span>
            <div className="flex-1 border-t border-dashed border-gray-300" />
          </div>
        )}

        {/* Real Hooks (draggable) */}
        {sortedHooks.map((hook) => {
          const linkedTab = HOOK_TO_TAB_MAPPING[hook.hook_name];
          const isExpanded = linkedTab ? expandedTabs.has(linkedTab) : false;

          return (
            <div key={hook.hook_name} className="flex items-center gap-1">
              <div className="flex-1">
                <HookItem
                  hook={hook}
                  isSelected={selectedHook?.hook_name === hook.hook_name}
                  isDragging={isDragging}
                  onSelect={() => onSelectHook(hook)}
                  onToggleVisibility={() => onToggleVisibility(hook)}
                />
              </div>
              {/* Navigate to tab button for linked hooks */}
              {linkedTab && isExpanded && onNavigateToTab && (
                <button
                  type="button"
                  onClick={() => onNavigateToTab(linkedTab)}
                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                  title={`前往配置`}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface HooksPipelineProps {
  agentType: string;
  userId?: string;
  /** Local config state from parent */
  localConfig: Partial<AgentConfiguration>;
  /** Callback to update local config */
  onConfigChange: (name: string, value: any) => void;
  /** Callback to navigate to a specific tab */
  onNavigateToTab?: (tabId: string) => void;
  /** Expanded tabs state for feature toggles */
  expandedTabs: Set<TabId>;
  /** Callback to toggle tab expansion */
  onToggleExpansion: (tabId: TabId) => void;
}

export const HooksPipeline: React.FC<HooksPipelineProps> = ({
  agentType,
  userId,
  localConfig,
  onConfigChange,
  onNavigateToTab,
  expandedTabs,
  onToggleExpansion,
}) => {
  const { messages } = useI18n();
  const t = messages.hooks;
  const [selectedHook, setSelectedHook] = useState<HookConfig | null>(null);

  // Dependency confirmation dialog state (for disabling)
  const [dependencyConfirmation, setDependencyConfirmation] = useState<{
    hookName: string;
    hookDisplayName: string;
    dependentHook: string;
    dependentDisplayName: string;
  } | null>(null);

  // Enable confirmation dialog state (for enabling a hook that requires another)
  const [enableConfirmation, setEnableConfirmation] = useState<{
    hookName: string;
    hookDisplayName: string;
    requiredHook: string;
    requiredDisplayName: string;
  } | null>(null);

  const {
    pipeline,
    isLoading,
    error,
    reorderHooks,
  } = useHooksPipeline({ agentType, userId });

  // Merge API pipeline data with local config for display
  const effectivePipeline = useMemo(() => {
    if (!pipeline) return null;

    // Override show_in_settings from localConfig.hooks_visible
    const hooksVisible = (localConfig as any)?.hooks_visible || {};

    const mergeHookWithLocalState = (hook: HookConfig): HookConfig => {
      const localVisibility = hooksVisible[hook.hook_name];
      // If local visibility is explicitly set, use it; otherwise use API value
      const showInSettings = localVisibility !== undefined ? localVisibility : hook.show_in_settings;
      return {
        ...hook,
        show_in_settings: showInSettings,
      };
    };

    const hooks = pipeline.hooks.map(mergeHookWithLocalState);
    const enabledCount = hooks.filter(h => h.show_in_settings).length;

    return {
      ...pipeline,
      hooks,
      system_hooks: pipeline.system_hooks.map(mergeHookWithLocalState),
      user_hooks: pipeline.user_hooks.map(mergeHookWithLocalState),
      final_hooks: pipeline.final_hooks.map(mergeHookWithLocalState),
      enabled_hooks: enabledCount,
    };
  }, [pipeline, localConfig]);

  // Toggle hook visibility - updates local config and linked tab
  const toggleHookVisibility = (hook: HookConfig) => {
    const newValue = !hook.show_in_settings;
    const linkedTab = HOOK_TO_TAB_MAPPING[hook.hook_name];

    // For hooks with linked tabs that are being collapsed:
    // Delegate to parent's onToggleExpansion which handles confirmation dialog
    // The parent will call back to update configs after user confirms
    if (linkedTab && !newValue && expandedTabs.has(linkedTab)) {
      // Just trigger the collapse confirmation in parent
      onToggleExpansion(linkedTab);
      return;
    }

    // Check if this hook has dependents that are visible/enabled (when disabling)
    if (!newValue) {
      const dependentInfo = HOOK_DEPENDENTS[hook.hook_name];
      if (dependentInfo) {
        // Check if the dependent hook is visible in the pipeline
        // First check hooks_visible, then fall back to effectivePipeline
        const hooksVisible = (localConfig as any)?.hooks_visible || {};
        const dependentVisible = hooksVisible[dependentInfo.dependentHook];

        // Also check if the hook exists in effectivePipeline and is shown
        const dependentInPipeline = effectivePipeline?.hooks.find(
          h => h.hook_name === dependentInfo.dependentHook
        );
        const isShown = dependentVisible !== undefined
          ? dependentVisible
          : (dependentInPipeline?.show_in_settings ?? false);

        if (isShown) {
          // Show confirmation dialog
          const hookDef = messages.hooks.hookDefinitions[hook.hook_name as keyof typeof messages.hooks.hookDefinitions];
          setDependencyConfirmation({
            hookName: hook.hook_name,
            hookDisplayName: hookDef?.displayName || hook.display_name,
            dependentHook: dependentInfo.dependentHook,
            dependentDisplayName: dependentInfo.dependentDisplayName,
          });
          return;
        }
      }
    }

    // Check if this hook requires another hook to be enabled first (when enabling)
    if (newValue) {
      const requiresInfo = HOOK_REQUIRES[hook.hook_name];
      if (requiresInfo) {
        // Check if the required hook is NOT visible in the pipeline
        const hooksVisible = (localConfig as any)?.hooks_visible || {};
        const requiredVisible = hooksVisible[requiresInfo.requiredHook];

        const requiredInPipeline = effectivePipeline?.hooks.find(
          h => h.hook_name === requiresInfo.requiredHook
        );
        const isShown = requiredVisible !== undefined
          ? requiredVisible
          : (requiredInPipeline?.show_in_settings ?? false);

        if (!isShown) {
          // Show enable confirmation dialog - required hook is OFF
          const hookDef = messages.hooks.hookDefinitions[hook.hook_name as keyof typeof messages.hooks.hookDefinitions];
          setEnableConfirmation({
            hookName: hook.hook_name,
            hookDisplayName: hookDef?.displayName || hook.display_name,
            requiredHook: requiresInfo.requiredHook,
            requiredDisplayName: requiresInfo.requiredDisplayName,
          });
          return;
        }
      }
    }

    // Execute the toggle
    executeHookToggle(hook.hook_name, newValue, linkedTab);
  };

  // Execute hook toggle (called directly or after confirmation)
  const executeHookToggle = (hookName: string, newValue: boolean, linkedTab?: TabId) => {
    onConfigChange(`hooks_visible.${hookName}`, newValue);

    // Sync hooks.*.enabled with visibility toggle
    // - When showing (true): enable the hook
    // - When hiding (false): disable the hook
    onConfigChange(`hooks.${hookName}.enabled`, newValue);

    // Special handling for knowledge_context: sync with knowledge_enabled
    if (hookName === 'knowledge_context') {
      onConfigChange('knowledge_enabled', newValue);
    }

    // If this hook has a linked tab, toggle the tab expansion
    if (linkedTab) {
      const isCurrentlyExpanded = expandedTabs.has(linkedTab);
      if (newValue && !isCurrentlyExpanded) {
        // Showing hook -> expand tab
        onToggleExpansion(linkedTab);
      }
    }
  };

  // Handle dependency confirmation - user confirmed to disable
  const handleConfirmDependencyDisable = () => {
    if (!dependencyConfirmation) return;

    const { hookName, dependentHook } = dependencyConfirmation;
    const linkedTab = HOOK_TO_TAB_MAPPING[hookName];
    const dependentLinkedTab = HOOK_TO_TAB_MAPPING[dependentHook];

    // Disable the main hook
    executeHookToggle(hookName, false, linkedTab);

    // Also disable the dependent hook (both visibility and enabled state)
    executeHookToggle(dependentHook, false, dependentLinkedTab);

    setDependencyConfirmation(null);
  };

  // Handle dependency confirmation cancellation
  const handleCancelDependencyDisable = () => {
    setDependencyConfirmation(null);
  };

  // Handle enable confirmation - user confirmed to continue (enable both hooks)
  const handleConfirmEnable = () => {
    if (!enableConfirmation) return;

    const { hookName, requiredHook } = enableConfirmation;
    const linkedTab = HOOK_TO_TAB_MAPPING[hookName];
    const requiredLinkedTab = HOOK_TO_TAB_MAPPING[requiredHook];

    // Enable the required hook first
    executeHookToggle(requiredHook, true, requiredLinkedTab);

    // Then enable this hook
    executeHookToggle(hookName, true, linkedTab);

    setEnableConfirmation(null);
  };

  // Handle enable confirmation cancellation
  const handleCancelEnable = () => {
    setEnableConfirmation(null);
  };

  const {
    activeId,
    isDragging,
    sensors,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    collisionDetection,
  } = useHookDragDrop({
    onReorder: async (items) => {
      if (items.length > 0) {
        await reorderHooks(items);
      }
    },
  });

  const handleDragEndWithReorder = async (event: any) => {
    const result = handleDragEnd(event);
    if (!result || !effectivePipeline) return;

    const { activeHookName, targetStage, overId } = result;

    const allHooks = effectivePipeline.hooks;
    const stageHooks = allHooks.filter((h) => h.stage === targetStage);
    const overIndex = stageHooks.findIndex(
      (h) => `hook-${targetStage}-${h.hook_name}` === overId
    );

    if (overIndex === -1) return;

    const newSortOrders = calculateNewSortOrders(
      allHooks,
      activeHookName,
      targetStage,
      overIndex
    );

    await reorderHooks(newSortOrders);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-500">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-red-200 bg-red-50 rounded-lg p-4">
        <h3 className="text-red-800 font-semibold mb-2">{t.errorLoading}</h3>
        <p className="text-red-600 text-sm">{(error as Error).message}</p>
      </div>
    );
  }

  if (!effectivePipeline) {
    return (
      <div className="text-center py-12 text-gray-500">
        {t.noData}
      </div>
    );
  }

  const activeHook = activeId
    ? effectivePipeline.hooks.find((h) => `hook-${h.stage}-${h.hook_name}` === activeId)
    : null;

  const stages: HookStageType[] = ['system', 'user', 'final'];

  // Count virtual features that are enabled
  const virtualEnabledCount = VIRTUAL_FEATURE_HOOKS.filter(f => {
    const value = getConfigValue(localConfig, f.configPath);
    return isFeatureEnabled(f.id as any, value);
  }).length;

  // Total count includes virtual + real hooks
  const totalEnabledCount = effectivePipeline.enabled_hooks + virtualEnabledCount;
  const totalHooksCount = effectivePipeline.total_hooks + VIRTUAL_FEATURE_HOOKS.length;

  return (
    <div className="flex gap-6">
      <div className="flex-1 space-y-4">
        {/* Pipeline Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t.pipelineTitle}</h3>
          <div className="text-sm text-gray-500">
            {t.enabledCount.replace('{enabled}', String(totalEnabledCount)).replace('{total}', String(totalHooksCount))}
          </div>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEndWithReorder}
          onDragCancel={handleDragCancel}
        >
          <div className="space-y-4">
            {stages.map((stage) => {
              const stageHooks =
                stage === 'system'
                  ? effectivePipeline.system_hooks
                  : stage === 'user'
                  ? effectivePipeline.user_hooks
                  : effectivePipeline.final_hooks;

              // For system stage, render virtual features first
              if (stage === 'system') {
                return (
                  <SystemStageWithVirtualFeatures
                    key={stage}
                    hooks={stageHooks}
                    selectedHook={selectedHook}
                    isDragging={isDragging}
                    onSelectHook={setSelectedHook}
                    onToggleVisibility={toggleHookVisibility}
                    translations={t}
                    localConfig={localConfig}
                    onConfigChange={onConfigChange}
                    expandedTabs={expandedTabs}
                    onToggleExpansion={onToggleExpansion}
                    onNavigateToTab={onNavigateToTab}
                  />
                );
              }

              return (
                <HookStage
                  key={stage}
                  stage={stage}
                  hooks={stageHooks}
                  selectedHook={selectedHook}
                  isDragging={isDragging}
                  onSelectHook={setSelectedHook}
                  onToggleVisibility={toggleHookVisibility}
                  translations={t}
                />
              );
            })}
          </div>

          <DragOverlay>
            {activeHook ? (
              <div className="opacity-80">
                <HookItem
                  hook={activeHook}
                  isSelected={false}
                  isDragging={false}
                  onSelect={() => {}}
                  onToggleVisibility={() => {}}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      <div className="w-96 flex-shrink-0">
        <HookConfigPanel
          hook={selectedHook}
          translations={t}
        />
      </div>

      {/* Dependency Confirmation Dialog (Disable) */}
      {dependencyConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop - no click handler to prevent accidental dismissal */}
          <div className="absolute inset-0 bg-black/50" />

          {/* Dialog */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  确认关闭
                </h3>
                <p className="text-gray-600 mb-4">
                  关闭「{dependencyConfirmation.hookDisplayName}」将导致依赖它的「{dependencyConfirmation.dependentDisplayName}」功能无法正常工作。
                  <br /><br />
                  是否同时关闭「{dependencyConfirmation.dependentDisplayName}」？
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={handleCancelDependencyDisable}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDependencyDisable}
                    className="px-4 py-2 text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors"
                  >
                    确认关闭
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enable Confirmation Dialog - when enabling a hook that requires another */}
      {enableConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop - no click handler to prevent accidental dismissal */}
          <div className="absolute inset-0 bg-black/50" />

          {/* Dialog */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  需要先启用依赖
                </h3>
                <p className="text-gray-600 mb-4">
                  「{enableConfirmation.hookDisplayName}」依赖「{enableConfirmation.requiredDisplayName}」的执行数据才能正常工作。
                  <br /><br />
                  是否继续启用？
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={handleCancelEnable}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmEnable}
                    className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    继续
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
